import { useCallback, useEffect } from "react";

export interface LightboxImage {
  src: string;
  alt: string;
}

interface LightboxProps {
  images: LightboxImage[];
  index: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

export default function Lightbox({ images, index, onClose, onNavigate }: LightboxProps) {
  const prev = useCallback(
    () => onNavigate((index - 1 + images.length) % images.length),
    [index, images.length, onNavigate],
  );
  const next = useCallback(
    () => onNavigate((index + 1) % images.length),
    [index, images.length, onNavigate],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);
    document.body.classList.add("no-scroll");
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.classList.remove("no-scroll");
    };
  }, [onClose, prev, next]);

  const current = images[index];
  if (!current) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-primary-950/90 p-4 md:p-10"
      role="dialog"
      aria-modal="true"
      aria-label="Görsel galeri"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 flex h-11 w-11 items-center justify-center rounded-full bg-background-50/20 text-background-50 transition-colors hover:bg-background-50/30"
        aria-label="Kapat"
      >
        <i className="ri-close-line text-2xl" aria-hidden="true"></i>
      </button>

      <button
        onClick={(e) => {
          e.stopPropagation();
          prev();
        }}
        className="absolute left-2 md:left-6 flex h-11 w-11 items-center justify-center rounded-full bg-background-50/20 text-background-50 transition-colors hover:bg-background-50/30"
        aria-label="Önceki görsel"
      >
        <i className="ri-arrow-left-line text-2xl" aria-hidden="true"></i>
      </button>

      <figure className="max-w-4xl" onClick={(e) => e.stopPropagation()}>
        <img
          src={current.src}
          alt={current.alt}
          className="mx-auto max-h-[78vh] w-auto rounded-lg object-contain"
        />
        <figcaption className="mt-4 text-center text-sm text-background-50/80">
          {index + 1} / {images.length}
        </figcaption>
      </figure>

      <button
        onClick={(e) => {
          e.stopPropagation();
          next();
        }}
        className="absolute right-2 md:right-6 flex h-11 w-11 items-center justify-center rounded-full bg-background-50/20 text-background-50 transition-colors hover:bg-background-50/30"
        aria-label="Sonraki görsel"
      >
        <i className="ri-arrow-right-line text-2xl" aria-hidden="true"></i>
      </button>
    </div>
  );
}